import { z } from 'zod'

import { methodSchema } from '@/agent'
import { baseRegistrySchema } from '@/registry'
import { scalarRuleset } from '@/rule'
import { docVersionSchema, nanoidSchema } from '@/utility'

export enum RegistryAsset {
  Apis = 'apis',
  Rules = 'rules',
  Sdks = 'sdks',
  Schemas = 'schemas',
  Workspaces = 'workspaces',
}

export enum ApiEmbedStatus {
  Complete = 'complete',
  Failed = 'failed',
}

export const managedDocTool = z.object({
  path: z.string(),
  method: methodSchema,
  enabledTools: z.enum(['execute-request', 'get-mini-openapi-spec']).array(),
})

export type ManagedDocTool = z.infer<typeof managedDocTool>

export const managedDocVersionSchema = z
  .object({
    uid: nanoidSchema,
    /** Millisecond base timestamp of versions  */
    createdAt: z.number(),
    version: docVersionSchema,
    /** OpenAPI version normalized on upload */
    upgraded: z.boolean().default(false),
    /** Vector embeddings processed, agent is enabled */
    embedStatus: z.enum(ApiEmbedStatus).nullable().default(null),
    tags: z.string().array().default([]),
    tools: managedDocTool.array().optional(),
  })
  .meta({ id: 'managed-doc-version' })

export type ManagedDocVersion = z.infer<typeof managedDocVersionSchema>

export const managedDocSchema = baseRegistrySchema
  .extend({
    /** The current version served to consumers by default */
    version: docVersionSchema,
    /**
     * Certain types of collections will be write restricted for properties
     * that would mutate the original specification (such as adding endpoints)
     */
    isExternal: z.boolean().default(true),
    /** Repository information if we are managing a github spec */
    repository: z
      .object({
        linkedBy: z.string(),
        id: z.number(),
        name: z.string().min(2),
        path: z.string().default(''),
        branch: z.string().default(''),
      })
      .optional(),
    /** URL to the source spec if we are managing a hosted spec */
    sourceUrl: z.url().optional(),
    /** Theme assigned to API, supports custom theme slugs */
    theme: z.string().default(''),
    /**
     * UID to the collaborative YJS doc
     *
     * This content will be replaced on submitted updates and able to be edited in real-time
     * When a user is ready to commit their changes they can bump the version with the changed content
     */
    editableUid: z.string(),
    ruleset: z.string().default(scalarRuleset),
    /**
     * An ordered version history record
     *
     * When a sync update is requested we find any versions with timestamps
     * later than the local base and determine the mutation records
     *
     * Mutations records will then need to be diffed and conflicts handled locally
     * before pushing to the database
     */
    versions: managedDocVersionSchema.array(),
    /**
     * Scalar verified status
     *
     * This will be moved in the future to a separate
     * collection for registry DNS checks.
     */
    verified: z.boolean().default(false),
  })
  .meta({ id: 'managed-doc' })

export type ManagedDoc = z.infer<typeof managedDocSchema>
